import type { OverviewChartRow } from "@nexus/contracts";
import { collectColumnFields, compileKpiFormula, type KpiColumnField } from "@nexus/kpi-formula";

/**
 * The column tokens the Overview chart can actually evaluate.
 *
 * `OverviewChartRow` carries 16 of the 17 `COLUMN_FIELDS` — it has no
 * `avg_watch_time`. `KpiNumericRow` is `Partial`, so a missing key reads as null
 * and nulls the whole expression, which would render as an empty bar with no
 * explanation. The Overview picker uses this to omit such KPIs instead.
 */
export const OVERVIEW_CHART_COLUMN_FIELDS = [
  "spend",
  "sticked",
  "value",
  "conversions",
  "clicks",
  "impressions",
  "link_click",
  "video_play_action",
  "video_thruplay_actions",
  "likes",
  "custom_event",
  "comments",
  "post_engagements",
  "creatives",
  "ads",
  "frequency",
] as const satisfies readonly (KpiColumnField & keyof OverviewChartRow)[];

const CHARTABLE = new Set<string>(OVERVIEW_CHART_COLUMN_FIELDS);

/**
 * Whether the Overview chart can compute this formula. A raw metric passes its
 * own name; an uncompilable formula is not chartable rather than throwing, since
 * `compileCompute` already degrades those to a null-producing metric.
 */
export function isChartableOnOverview(formula: string | null, name: string): boolean {
  try {
    const fields = collectColumnFields(compileKpiFormula(formula ?? name));
    return fields.every((field) => CHARTABLE.has(field));
  } catch {
    return false;
  }
}
